import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { HTTP_METHOD, PRODUCTS_ENDPOINT } from './_shared';

export type MakeGetProductsByCodesDeps<ProductsByCodes extends SchemaType.Top> = {
  readonly schemas: {
    readonly productsByCodes: ProductsByCodes;
  };
};

type GetProductsByCodesProps = {
  codes: readonly string[];
  page: number;
  itemsPerPage: number;
};

export const makeGetProductsByCodes = <ProductsByCodes extends SchemaType.Top>(
  deps: MakeGetProductsByCodesDeps<ProductsByCodes>
) => {
  const GetProductsByCodesError = createBackendApiError({
    domain: 'Product',
    endpoint: 'GetProductsByCodes',
    path: PRODUCTS_ENDPOINT,
    identifierField: 'codes',
  });

  const getProductsByCodesFetch = ({ codes, page, itemsPerPage }: GetProductsByCodesProps) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(PRODUCTS_ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          query: {
            'code[]': [...codes],
            page,
            itemsPerPage,
            inStock: true,
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${PRODUCTS_ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'product.codes': codes.join(','),
          'product.page': page,
          'product.itemsPerPage': itemsPerPage,
        },
      })
    );

  const getProductsByCodes = (props: GetProductsByCodesProps) =>
    getProductsByCodesFetch(props).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.schemas.productsByCodes))(response),
          404: (response) =>
            Effect.fail(
              new GetProductsByCodesError.NotFoundError({ codes: props.codes, response })
            ),
          orElse: (response) =>
            Effect.fail(
              new GetProductsByCodesError.ApiRequestError({ codes: props.codes, response })
            ),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductGet,
          'Debug',
          `Successfully resolved ${String(result['hydra:totalItems'])} products for ${String(props.codes.length)} codes`
        )
      ),
      Effect.withSpan('api.products.getByCodes', {
        kind: 'internal',
        attributes: {
          'product.codes': props.codes.join(','),
          'product.page': props.page,
          'product.itemsPerPage': props.itemsPerPage,
        },
      })
    );

  return { getProductsByCodes, GetProductsByCodesError };
};
